/**
 * THE DOCUMENT RAIL AND THE DOCUMENT LIST.
 *
 * The rail answers "what kind of thing am I looking for" and the list answers
 * "which one". Both are built only from rows the backend actually returned:
 * a category with no documents does not appear, because an empty bucket is a
 * claim that this project has that kind of document.
 *
 * A ROW IS A SUMMARY, NOT A CONTROL PANEL. The page this replaces put two
 * selects, a number input and two icon buttons on every row, which is what
 * made it 764px wide on a 412px phone and what made a keyboard user tab
 * through five controls to reach the second document. Every mutation now
 * lives in the context panel, against the one document being looked at.
 *
 * Status is never colour alone: each row prints the status word and a
 * non-colour mark, so a monochrome screen in daylight still reads.
 */
import { useCallback, useRef } from 'react';
import { FolderOpen, Search, AlertTriangle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { DocumentTypeIcon } from './DocumentPreview';
import { nextIndexForKey } from './documentPresentation';

const TONE_CLASS = {
  settled: 'text-emerald-400',
  attention: 'text-amber-400',
  against: 'text-red-400',
  retired: 'text-purple-300',
  neutral: 'text-muted-foreground',
  unknown: 'text-muted-foreground',
};

/** Status word + non-colour mark. The word is the meaning; colour is a hint. */
export const StatusMark = ({ display, className = '' }) => (
  <span
    className={`inline-flex items-center gap-1 ${TONE_CLASS[display.tone] || TONE_CLASS.neutral} ${className}`}
  >
    <span aria-hidden="true" className="font-mono text-[0.7rem] leading-none">
      {display.mark}
    </span>
    <span className="text-[0.7rem] font-medium uppercase tracking-wide">
      {display.label}
    </span>
  </span>
);

export const DocumentRail = ({ buckets, activeCategory, onSelectCategory }) => (
  <nav
    aria-label="Document types"
    data-testid="document-rail"
    className="flex flex-col gap-0.5"
  >
    {buckets.map((bucket) => {
      const active = bucket.value === activeCategory;
      return (
        <button
          key={bucket.value}
          type="button"
          data-testid={`document-rail-${bucket.value}`}
          aria-current={active ? 'true' : undefined}
          onClick={() => onSelectCategory(bucket.value)}
          className={`flex items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            active
              ? 'bg-primary/15 font-semibold text-foreground'
              : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'
          }`}
        >
          <span className="truncate">{bucket.label}</span>
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 font-mono text-[0.7rem] text-muted-foreground">
            {bucket.count}
          </span>
        </button>
      );
    })}
  </nav>
);

/**
 * @param {Array} documents already filtered, in display order
 * @param {string|null} selectedId
 * @param {function} onSelect
 * @param {Array} unreadable rows the contract reader could not accept
 */
export const DocumentList = ({
  documents,
  selectedId,
  onSelect,
  unreadable = [],
  compact = false,
  emptyHint,
}) => {
  const listRef = useRef(null);

  /**
   * Arrow keys move focus down a long rail of drawings without tabbing
   * through every row. Every row button also stays in the tab order, so this
   * is an accelerator rather than the only way in — a roving tabindex that
   * loses its anchor would leave a keyboard user with no way back.
   */
  const onKeyDown = useCallback(
    (event, index) => {
      const next = nextIndexForKey(event.key, index, documents.length);
      if (next === null) return;
      event.preventDefault();
      const node = listRef.current?.querySelectorAll('[data-document-row]')[
        next
      ];
      if (node) node.focus();
    },
    [documents.length],
  );

  if (documents.length === 0) {
    return (
      <div
        data-testid="document-list-empty"
        className="px-4 py-10 text-center text-muted-foreground"
      >
        <FolderOpen
          className="mx-auto mb-3 h-8 w-8 opacity-50"
          aria-hidden="true"
        />
        <p className="text-sm font-medium text-foreground">
          No documents match
        </p>
        <p className="mt-1 text-xs">
          {emptyHint || 'Adjust the search or category, or upload a file.'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {unreadable.length > 0 && (
        /*
          MALFORMED IS NOT EMPTY, and it is not invisible either. A row the
          contract reader refused is counted here rather than silently
          dropped: "12 documents" when the backend sent 13 is a quiet lie
          about what is on this project.
        */
        <div
          data-testid="document-list-unreadable"
          className="flex items-start gap-2 border-b border-amber-500/30 bg-amber-500/5 px-4 py-2 text-xs text-muted-foreground"
        >
          <AlertTriangle
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400"
            aria-hidden="true"
          />
          <span>
            <span className="font-medium text-foreground">
              {unreadable.length} record{unreadable.length === 1 ? '' : 's'}{' '}
              could not be read.
            </span>{' '}
            They are not shown and are not counted as documents. This is a
            payload problem, not an empty project.
          </span>
        </div>
      )}
      <ul
        ref={listRef}
        data-testid="document-list"
        className="divide-y divide-border"
      >
        {documents.map((doc, index) => {
          const selected = doc.id === selectedId;
          return (
            <li key={doc.id}>
              <button
                type="button"
                data-document-row
                data-testid={`document-row-${doc.id}`}
                data-selected={selected ? 'true' : 'false'}
                aria-current={selected ? 'true' : undefined}
                onClick={() => onSelect(doc.id)}
                onKeyDown={(event) => onKeyDown(event, index)}
                className={`flex w-full items-start gap-3 px-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                  compact ? 'py-2' : 'py-3'
                } ${selected ? 'bg-primary/10' : 'hover:bg-muted/40'}`}
              >
                <span
                  className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
                    selected
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted/60 text-muted-foreground'
                  }`}
                >
                  <DocumentTypeIcon iconKey={doc.iconKey} className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                      {doc.name}
                    </span>
                    <span className="shrink-0 font-mono text-[0.7rem] text-muted-foreground">
                      {doc.versionLabel}
                    </span>
                  </span>
                  <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <StatusMark display={doc.statusDisplay} />
                    <StatusMark display={doc.reviewStatusDisplay} />
                    <span className="text-[0.7rem] text-muted-foreground">
                      {doc.categoryLabel} · {doc.fileSizeLabel}
                    </span>
                    {/*
                      The availability of the FILE is a separate fact from the
                      standing of the RECORD, so it gets its own chip and is
                      only drawn when it is something a person must know:
                      a link that was not issued, or a record with no file.
                    */}
                    {(doc.availability.state === 'LINK_NOT_ISSUED' ||
                      doc.availability.state === 'NO_STORED_OBJECT') && (
                      <span
                        data-testid={`document-availability-${doc.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 px-1.5 py-0.5 text-[0.65rem] text-amber-300"
                      >
                        <span
                          aria-hidden="true"
                          className="font-mono leading-none"
                        >
                          {doc.availability.display.mark}
                        </span>
                        {doc.availability.display.label}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const DocumentSearch = ({ value, onChange, resultCount }) => (
  <div className="relative">
    <Search
      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
      aria-hidden="true"
    />
    <Input
      type="search"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder="Search documents…"
      aria-label="Search documents by name, description, category or file type"
      data-testid="document-search"
      className="bg-muted/50 pl-9"
    />
    <span className="sr-only" role="status" aria-live="polite">
      {resultCount} document{resultCount === 1 ? '' : 's'} listed
    </span>
  </div>
);

export default DocumentList;
